// --------------------------
// IMPORTS
// --------------------------
import { createContext, useContext, useEffect, useState } from "react";
import "./App.css";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { message } from "antd";

import secureLocalStorage from "react-secure-storage";

//Components
import Survey from "./crm/views/voc/survey/survey";
import AuditForms from "./crm/views/qms/auditForms/auditForms";

import LogoutHandler from "./crm/views/logoutHandler/logoutHandler";
import { componentMap, roles } from "./routes";
import { GlobalStateContext } from "./crm/utils/GlobalStateContext";
import NotFound from "./crm/views/layouts/NotFound";
import UnderMaintenance from "./crm/views/layouts/UnderMaintenance";
import Main from "./crm/views/layouts/main/main";
import { validateAndLoginThunk } from "./crm/store/thunks/authThunk";
import { useDispatch } from "react-redux";
import Dashboard from "./crm/views/common/dashboard/dashboard";
import { useSelector } from "react-redux";
import CustomLoader from "./crm/views/common/customLoader";

const UserContext = createContext();

// --------------------------
// MAIN COMPONENT
// --------------------------
const App = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname?.split("/");
  const { setGlobalState } = useContext(GlobalStateContext);
  // --------------------------
  // REDUX STATE
  // --------------------------
  const { isLoggedIn, loading, role } = useSelector((state) => state.auth);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  // const [isLoggedin, setIsLoggedin] = useState(false);
  // const [loading, setLoading] = useState(true);

  //const userRole = secureLocalStorage.getItem("role");

  // Define your components - Forbidden, AuditForms, Audits, AuditView, Users, AddUser, Roles, AddRole

  const publicRoutes = ["/survey", "/logout", "/underMaintenance"];
  const isPublicRoute = publicRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  const hasAccess = (role) => {
    if (window.performance) {
      const entries = performance.getEntriesByType("navigation");

      if (entries.length > 0 && entries[0].type === "reload") {
        const searchKeywordOnRefresh = document.querySelector(".inputS");
        if (
          searchKeywordOnRefresh?.value === "" ||
          searchKeywordOnRefresh?.value === undefined
        ) {
          if (secureLocalStorage.getItem("hasSearchedValue")) {
            secureLocalStorage.removeItem("hasSearchedValue");
          }
          if (secureLocalStorage.getItem("searchKeyword")) {
            secureLocalStorage.removeItem("searchKeyword");
          }
        }

        if (
          (location.pathname.split("/")[1] !== "users" ||
            location.pathname.split("/")[1] !== "roles") &&
          location.pathname.split("/")[2]
        ) {
          // Get elements by class name
          let sideNavElements = document.getElementsByClassName("sidenav");
          let rightSideElements = document.getElementsByClassName("right-side");

          // Check if there are elements with the specified class
          if (sideNavElements.length > 0) {
            // Access the first element in the collection and toggle the class
            sideNavElements[0].classList.remove("open");
          }

          if (rightSideElements.length > 0) {
            // Access the first element in the collection and toggle the class
            rightSideElements[0].classList.remove("right-side-open");
          }
        }
      }
    }

    return roles[role] || [];
  };

  const accessibleRoutes = role ? hasAccess(role) : ["/underMaintenance"];

  // --------------------------
  // USE EFFECTS
  // --------------------------

  // --------------------------
  // INITIAL AUTH CHECK
  // --------------------------
  useEffect(() => {
    const loginWithToken = async () => {
      const accessToken = secureLocalStorage.getItem("accessToken");
      const isSurveyPage = location.pathname.startsWith("/survey");

      const token = new URLSearchParams(location.search).get("token");

      if (token) {
        try {
          await dispatch(validateAndLoginThunk({ token })).unwrap();

          setGlobalState((prev) => ({
            ...prev,
            isLoggedIn: true,
          }));

          if (location.pathname === "/") {
            navigate("/dashboard", { replace: true });
          }
        } catch (err) {
          // showNotification(
          //   "The email you used does not exist in our system. Please logout and try logging in with a different account.",
          // );
          clearAndLogout();
        }
      } else {
        console.log("Failed Token");
        const logoutReq = pathName[1] === "logout" ? true : false;
        if (!accessToken && !logoutReq && !isPublicRoute) {
          window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
        }
      }
    };
    loginWithToken();
  }, [location.search]);

  //To show notification using Ant Design's notification component
  const showNotification = (errorMsg, moreThan = "no") => {
    message.error({
      content: (
        <div style={{ whiteSpace: "pre-line" }}>
          {moreThan === "yes" ? (
            <strong>Errors occurred:</strong>
          ) : (
            <strong>An error occurred:</strong>
          )}
          <p>{errorMsg}</p> {/* This ensures line breaks are respected */}
        </div>
      ),
      duration: 5, // duration in seconds
      className: "error-message", // Add any custom styles you need
    });
  };

  const clearAndLogout = async () => {
    secureLocalStorage.clear();
    localStorage.setItem("logout", Date.now()); // Triggers storage event
    //window.location.href = process.env.REACT_APP_SSO_LOGIN_REDIRECT_URI;
  };

  // Render loading indicator if still loading
  if (loading) {
    return <div className="app-loader"></div>;
  }

  return (
    <UserContext.Provider
      value={{
        loggedIn: isLoggedIn,
        // loginSetter: () => {
        //   setIsLoggedin(!isLoggedin);
        // },
      }}
    >
      <Routes>
        {/* ROOT */}
        <Route
          path="/"
          element={
            loading ? (
              <CustomLoader showLoader={true} />
            ) : isLoggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/underMaintenance" replace />
            )
          }
        />
        {/* DASHBOARD */}
        <Route
          path="/dashboard"
          element={
            isLoggedIn ? (
              <Main
                children={<Dashboard />}
                key_elem={"1"}
                nameLink="Dashboard"
              />
            ) : (
              <Navigate to="/underMaintenance" replace />
            )
          }
        />
        {/* PUBLIC */}
        <Route path="/survey/:id" element={<Survey />} />
        <Route path="/logout/:empID" element={<LogoutHandler />} />
        <Route path="/underMaintenance" element={<UnderMaintenance />} />
        {/* ROLE BASED ROUTES */}
        {accessibleRoutes.map((route) => (
          <Route
            key={route}
            path={route}
            element={
              isLoggedIn ? (
                componentMap[route]
              ) : (
                <Navigate to="/underMaintenance" replace />
              )
            }
          />
        ))}
        {/* 404 */}
        <Route path="/404" element={<NotFound />} />
        <Route
          path="*"
          element={<Navigate to={componentMap.noRoute} replace />}
        />{" "}
      </Routes>
    </UserContext.Provider>
  );
};

export default App;
export { UserContext };
